import { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { Confirm, useRefresh } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import useDeleteResource from '@app/hooks/useDeleteResource';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { Box, Tab, Tabs, Typography } from '@mui/material';

import { ListHeader } from '@app/components/ListHeader';
import { HeaderAction, HeaderActionTypes, ListHeaderActions } from '@app/components/ListHeaderActions';
import { DefaultTable } from '@app/components/common/Table/DefaultTable';
import {
  ActionInterface,
  CUSTOM_ICON_TYPES,
  ColumnInterface,
  DataSource,
  DeleteConfirmation,
  GenericObject,
} from '@app/components/common/Table/DefaultTable/types';

import './styles.css';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}>
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

interface GenericListProps {
  resource: string;
  title: string;
  resourceSingular: string;
  dataSources?: DataSource[];
  columns: ColumnInterface[];
  orderable?: boolean;
  generalFilterKey?: string;
  noDefaultHeaderActions?: boolean;
  hideSearch?: boolean;
  isListLoading: boolean;
  additionalHeaderActions?: HeaderAction[];
  replacementHeaderActions?: HeaderAction;
  additionalTableActions?: ActionInterface[];
  page?: number;
  SearchFields?: FunctionComponent;
  rowsPerPage?: number;
  generalFilterLabel?: string;
  total?: number;
  defaultSource: string;
  handleChangePage?(event: React.MouseEvent<HTMLButtonElement> | null, page: number): void;
  handleChangeRowsPerPage?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  reorderRecord?(dragIndex: number, hoverIndex: number): void;
  handleChangeLoading(loading: boolean): void;
  deleteCallback?(deletingId: number): void;
  handleChangeFilters?(filters: GenericObject): void;
  resolveDeleteConfirmationContent?(deletingId: number): DeleteConfirmation;
  onSelectedSource?(source: string): void;
  isRowEditable?: Function;
}

export const TabbedGenericList = ({
  resource,
  resourceSingular,
  total,
  generalFilterLabel,
  handleChangeLoading,
  title,
  columns,
  reorderRecord,
  generalFilterKey,
  hideSearch,
  orderable,
  isListLoading,
  noDefaultHeaderActions,
  additionalHeaderActions,
  additionalTableActions,
  SearchFields,
  page = 0,
  rowsPerPage = 10,
  handleChangePage,
  handleChangeRowsPerPage,
  deleteCallback,
  handleChangeFilters,
  dataSources,
  resolveDeleteConfirmationContent,
  onSelectedSource,
  defaultSource,
  isRowEditable,
}: GenericListProps) => {
  const refresh = useRefresh();
  const navigate = useNavigate();

  const {
    onDelete,
    deleteContent,
    isDeleteConfirmationOpen,
    isDeleteLoading,
    handleConfirmDelete,
    handleDeleteDialogClose,
  } = useDeleteResource(resource, resourceSingular, deleteCallback, resolveDeleteConfirmationContent);
  const ability = useAbility();

  const [value, setValue] = useState(0);
  const [source, setSource] = useState(dataSources?.[0].source || '');
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSource(dataSources?.[newValue].source || '');
    if (onSelectedSource) {
      onSelectedSource(dataSources?.[newValue].source || '');
    }
  };

  useEffect(() => {
    handleChangeLoading(!!isListLoading);
  }, [isListLoading, handleChangeLoading]);

  useEffect(() => {
    setSource(defaultSource);
    setValue(dataSources?.findIndex((d) => d.source === defaultSource) || 0);
  }, [defaultSource, dataSources]);

  const onEdit = (row: GenericObject) => {
    navigate(`/${resource}/${row.id}`);
  };

  const onCreateNew = useCallback(() => {
    navigate(`/${resource}/create?type=${source}`);
  }, [navigate, resource, source]);

  const defaultTableActions = [];

  if (ability.can(CASLAction.WRITE, resource)) {
    defaultTableActions.push({ id: 'edit', icon: () => CUSTOM_ICON_TYPES.EDIT, action: onEdit, label: 'Edit' });
  }

  if (ability.can(CASLAction.DELETE, resource)) {
    defaultTableActions.push({ id: 'delete', icon: () => CUSTOM_ICON_TYPES.DELETE, action: onDelete, label: 'Delete' });
  }

  let tableActions = defaultTableActions;

  const canAddNew = ability.can(CASLAction.WRITE, resource);

  const defaultHeaderActions = useMemo(
    () => (canAddNew ? [{ id: 1, type: HeaderActionTypes.ADD_NEW, handler: onCreateNew }] : []),
    [onCreateNew, canAddNew]
  );

  const headerActions = useMemo(() => {
    if (noDefaultHeaderActions) {
      return additionalHeaderActions ?? [];
    }

    if (additionalHeaderActions) {
      return [...defaultHeaderActions, ...additionalHeaderActions];
    }

    return defaultHeaderActions;
  }, [additionalHeaderActions, defaultHeaderActions, noDefaultHeaderActions]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="generic-list_container">
      <ListHeader
        hideSearch={hideSearch}
        generalFilterLabel={generalFilterLabel}
        generalFilterKey={generalFilterKey}
        title={title}
        SearchFields={SearchFields}
        handleChangeFilters={handleChangeFilters}
      />
      <ListHeaderActions headerActions={headerActions} />

      <Box>
        <Tabs value={value} onChange={handleChange}>
          {dataSources?.map(({ source }) => (
            <Tab label={source} />
          ))}
        </Tabs>
      </Box>
      {dataSources?.map(({ rows }, index) => (
        <TabPanel value={value} index={index}>
          <DefaultTable
            resource={resource}
            title={title}
            reorderRecord={reorderRecord}
            orderable={orderable}
            rows={rows}
            columns={columns}
            total={total}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            isRowEditable={isRowEditable}
            actions={additionalTableActions ? [...additionalTableActions, ...tableActions] : tableActions}
          />
        </TabPanel>
      ))}

      <Confirm
        isOpen={isDeleteConfirmationOpen}
        loading={isDeleteLoading}
        title={`Delete ${resourceSingular}`}
        content={deleteContent as string}
        onConfirm={handleConfirmDelete}
        cancel="Cancel"
        confirm="Delete"
        onClose={handleDeleteDialogClose}
      />
    </div>
  );
};
